/**
 * useAutoShutter — 라이브 검출 이벤트 기반 자동 셔터.
 *
 * 설계 (2026-08-01 — 자동 매칭 루프를 자동 촬영으로 대체. 판정 완화 동일자):
 *  - 검출 quad 가 있는(+ 선명한) 프레임이 **WINDOW_MS(2초) 안에 REQUIRED_HITS(3)회**
 *    쌓이면 셔터를 자동으로 누른 것처럼 정지 화면으로 전환한다.
 *    ⚠ "2초 연속 유지" 방식은 실기기에서 거의 발화하지 않았다 — seg 검출이 프레임마다
 *    붙었다 끊겼다 해서 한 번만 놓쳐도 타이머가 리셋됐다(사용자 실측). 그래서 미검출
 *    프레임은 리셋이 아니라 **슬라이딩 창에서 자연 만료**시키고 히트 횟수로 판정한다.
 *  - **가이드 기하 체크는 없다**(2026-08-20 제거 — 이력은 handleDetection 주석 참조).
 *    "언제 찍을지"는 [matchGate](매칭 확신)가 정한다.
 *  - 매칭은 여기서 하지 않는다. 다만 **발화 조건으로는 쓴다** — [matchGate] 가 주어지면
 *    검출 히트가 충족돼도 게이트가 열릴 때만 촬영한다(2026-08-16). 기하만으로
 *    찍던 종전 방식은 "카드 모양이면 찍는다" 라 라이브 quad 가 거친 프레임까지 스틸로
 *    넘겼다. 호출부(ScanPage)가 라이브 힌트 매칭의 프레임 간 일관성(streak)을 게이트로
 *    물린다 — 즉 **"이 카드다"가 몇 프레임 연속 나올 때 셔터가 눌린다**.
 *  - status 는 **현재 프레임** 기준: searching(미검출) / holding(검출됨).
 *  - 촬영은 1회(single-flight). 촬영 스틸의 재검출 코너를 onCaptured 로 넘기고,
 *    스틸 재검출이 실패하면 마지막 라이브 코너를 폴백으로 쓴다.
 */
import { useCallback, useRef, useState } from "react";
import { nativeWarn } from "../../native";
import type { CaptureAndCropResult, DetectionEvent } from "../../native/liveVision";

/** 히트를 세는 슬라이딩 창 */
export const AUTO_SHUTTER_WINDOW_MS = 2000;
/**
 * 창 안에서 이만큼 모이면 발화 (연속일 필요 없음). 3→2 (2026-08-27 사용자 결정 —
 * 확정 빈도가 너무 낮다): 구형 기기(Note 9, 검출 1.5~1.8fps)에서 3히트는 ~2초라
 * 셔터 자체가 병목이었다. 성급한 스틸의 오답 위험은 셔터가 아니라 확정 게이트
 * (노출 게이트·still 재판정·백오프)가 막는다 — 손해는 사진 한 장 재시도다.
 */
export const AUTO_SHUTTER_REQUIRED_HITS = 2;
/**
 * 히트 인정 최소 선명도 (엔진 Laplacian 분산) — 모션블러 프레임으로 촬영해 매칭
 * 정확도가 떨어지는 것을 막는다. **fail-open**: 엔진이 sharpness 를 안 주면(iOS rect,
 * 구 엔진) 게이트 없이 통과. 값은 보수적으로 낮게 시작 — 실기기 로그로 튜닝 대상.
 */
export const AUTO_SHUTTER_MIN_SHARPNESS = 30;
/**
 * 촬영 single-flight 를 강제로 푸는 상한 (2026-08-18 회귀 대응).
 * capture 가 끝나야 capturingRef 가 풀리는데, 그 안에서 늦게 끝나는 작업(OCR 스틸 등)이
 * 걸리면 **세션 내내 자동 촬영이 죽는다** — 실제로 그렇게 막혔다. 촬영 1회가 이 시간을
 * 넘기면 비정상으로 보고 잠금을 풀어 다음 기회를 준다.
 */
export const AUTO_SHUTTER_CAPTURE_TIMEOUT_MS = 6000;

export type AutoShutterStatus = "searching" | "holding";

export interface AutoCapturePayload {
  /** 전체 스틸 (file:// — 소유권 이관, 호출부가 정리) */
  path: string;
  width: number;
  height: number;
  /** 모델(테두리 인식)이 제시한 코너 — 스틸 재검출 우선, 실패 시 라이브 마지막 코너 */
  modelCorners: Array<{ x: number; y: number }> | null;
  /**
   * 스틸에서 검출된 quad 전부 (정규화 코너, 검출기 랭킹 순 — [0]=modelCorners 카드).
   * 다중 카드 확정(2026-08-26) 용. 구 네이티브·라이브 폴백이면 null — 소비자는
   * modelCorners 단일로 폴백한다.
   */
  modelQuads: Array<Array<{ x: number; y: number }>> | null;
}

interface UseAutoShutterOptions {
  enabled: boolean;
  /**
   * 발화 여부 — false 면 히트 판정·status(정렬 안내 칩)는 그대로 돌되 **자동 촬영만
   * 하지 않는다** (수동 셔터 전용 운용, 2026-08-15). 생략 시 true.
   */
  autoFire?: boolean;
  /**
   * 발화 직전 매칭 확신 게이트 — false 를 주면 검출 히트가 충족돼도 촬영하지 않는다.
   * 생략하면 게이트 없음(검출만으로 발화).
   * ⚠ 호출부는 **fail-closed** 로 구현할 것 — 힌트를 못 받는 환경(iOS rect 엔진,
   * matcher 미발행)에서 게이트가 항상 열리면 "카드 모양이면 찍는" 동작으로 되돌아간다.
   */
  matchGate?: () => boolean;
  /** useScanSession.capture — busy 시 null */
  capture: () => Promise<CaptureAndCropResult | null>;
  onCaptured: (payload: AutoCapturePayload) => void;
  deleteFile: (path: string | null | undefined) => void;
  /** 수동 촬영 진행 중 — 자동 셔터 일시 중지 (상호 배제) */
  suspendedRef?: { current: boolean };
}

export function useAutoShutter({
  enabled,
  autoFire = true,
  matchGate,
  capture,
  onCaptured,
  deleteFile,
  suspendedRef,
}: UseAutoShutterOptions): {
  status: AutoShutterStatus;
  /** useScanSession 의 onDetection 에 연결한다 */
  handleDetection: (ev: DetectionEvent) => void;
  /**
   * 발화까지의 진행도 0~1 (= 히트 / [AUTO_SHUTTER_REQUIRED_HITS]).
   * **셔터를 당기는 그 신호**를 그대로 내보낸다 — 표시가 다른 신호를 그리면
   * "다 안 찼는데 찍힌다"가 된다(2026-08-26 제보: 소리가 먼저, 채움이 나중).
   * 게이트가 닫혀 있으면 0.9 에서 멈춘다 — **가득 = 찍힌다** 를 지킨다.
   */
  progressRef: { current: number };
  /**
   * 히트·채움 리셋 — 브래킷 타깃이 **다른 객체**로 넘어갈 때 호출한다 (2026-08-27).
   * 히트 창은 전역이라 그대로 두면 새 카드가 이전 카드의 히트를 물려받아
   * 채움·발화가 이어져 보인다. 객체 단위로 다시 쌓게 한다.
   */
  resetHits: () => void;
} {
  const [status, setStatus] = useState<AutoShutterStatus>("searching");
  /** 가이드 안 검출 프레임의 타임스탬프 (슬라이딩 창) */
  const hitsRef = useRef<number[]>([]);
  /** 최근 검출 이벤트 간격(ms) — 창 크기를 기기 검출 속도에 적응시키는 근거 */
  const gapsRef = useRef<number[]>([]);
  const lastEventAtRef = useRef(0);
  const capturingRef = useRef(false);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCornersRef = useRef<Array<{ x: number; y: number }> | null>(null);
  const progressRef = useRef(0);

  // 콜백 최신본 참조 — handleDetection 을 안정 참조로 유지
  const optionsRef = useRef({
    enabled, autoFire, matchGate, capture, onCaptured, deleteFile,
  });
  optionsRef.current = {
    enabled, autoFire, matchGate, capture, onCaptured, deleteFile,
  };

  const fire = useCallback(async () => {
    const opts = optionsRef.current;
    try {
      const shot = await opts.capture();
      if (!shot) return; // busy/실패 — hold 는 이미 리셋됨, 다음 유지에서 재시도
      if (shot.cropPath) opts.deleteFile(shot.cropPath); // crop 은 미사용
      if (!shot.fullPath || !shot.fullWidth || !shot.fullHeight) {
        opts.deleteFile(shot.fullPath);
        return;
      }
      // 스틸 재검출 성공 → 그 코너(스틸과 정합), 실패 → 라이브 마지막 코너 폴백
      const modelCorners =
        shot.detected && shot.corners ? shot.corners : lastCornersRef.current;
      const modelQuads =
        shot.detected && shot.quads && shot.quads.length > 0
          ? shot.quads.map((q) => q.corners)
          : null;
      opts.onCaptured({
        path: shot.fullPath,
        width: shot.fullWidth,
        height: shot.fullHeight,
        modelCorners,
        modelQuads,
      });
    } catch (err) {
      nativeWarn("scan:autoShutter", err);
    } finally {
      if (unlockTimerRef.current) {
        clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = null;
      }
      capturingRef.current = false;
    }
  }, []);

  const resetHits = useCallback(() => {
    hitsRef.current = [];
    progressRef.current = 0;
  }, []);

  const handleDetection = useCallback(
    (ev: DetectionEvent) => {
      const opts = optionsRef.current;
      if (!opts.enabled || capturingRef.current) return;
      if (suspendedRef?.current) {
        hitsRef.current = [];
        lastCornersRef.current = null;
        progressRef.current = 0;
        setStatus("searching");
        return;
      }

      // 창 밖 히트는 만료 — 미검출/가이드 밖 프레임은 리셋이 아니라 시간이 지우게 둔다.
      // ⚠ 창은 고정 2초가 아니라 **기기 검출 주기에 적응**한다(2026-08-26 Note 9).
      // 2초/3회는 검출 ~10fps 전제인데, 구형 기기는 OBB 추론만 500~900ms 라 히트가
      // 쌓이는 속도가 만료 속도를 못 이겨 셔터가 원리적으로 발화하지 않았다. 최근
      // 이벤트 간격의 중앙값 × 필요 히트 × 2 까지 창을 늘린다 — 빠른 기기는 종전
      // 2초 그대로(하한), 상한은 두지 않는다: 창이 길어지는 만큼 "그 카드를 계속
      // 대고 있었다"는 조건은 유지되므로 오발화가 늘지는 않는다.
      const now = ev.timestampMs;
      // ⚠ 간격은 **검출된** 프레임 사이에서만 잰다 (아래 quad 체크 뒤에서 기록).
      // 창을 늘리는 근거가 "이 기기가 카드를 얼마나 자주 잡아 주는가"이기 때문이다.
      // 2026-08-31 에 엔진이 카드 없는 프레임의 검출을 건너뛰기 시작하면서(무검출
      // cadence) 빈 이벤트가 카메라 fps 로 빨라졌는데, 그것까지 세면 카드가 막
      // 들어온 순간의 중앙값이 30ms 대로 눌려 창이 하한으로 떨어진다. 종전에는
      // 빈 프레임 간격 = 검출 간격이라 같은 값이었어서 드러나지 않던 자리다.
      const sortedGaps = [...gapsRef.current].sort((a, b) => a - b);
      const medianGap = sortedGaps.length > 0 ? sortedGaps[sortedGaps.length >> 1] : 0;
      const windowMs = Math.max(
        AUTO_SHUTTER_WINDOW_MS,
        medianGap * AUTO_SHUTTER_REQUIRED_HITS * 2,
      );
      hitsRef.current = hitsRef.current.filter((t) => now - t < windowMs);

      const quad = ev.detected && ev.quads.length > 0 ? ev.quads[0] : null;
      if (!quad || quad.corners.length !== 4) {
        progressRef.current = 0;
        setStatus("searching");
        return;
      }
      // 가이드 기하 체크(가이드 안 + 4모서리 근접)는 두지 않는다 — 검출만 되면 히트다.
      // 이력: 2026-08-04 도입(멀리 있는 작은 카드의 저해상 촬영 배제) → 2026-08-19 제거
      // → 2026-08-20 오전 인식 저하 우려로 복원 → **2026-08-20 사용자 결정으로 최종 제거**.
      // "○○ 인식됨"(확신 매칭, matchGate 통과)이 떠 있는데도 기하에 막혀 셔터가 안
      // 눌리는 것이 더 큰 문제였다. 언제 찍을지는 matchGate(매칭 확신)가 단독으로 정한다.

      // 검출된 프레임 사이의 간격만 창 산정에 쓴다 (위 주석)
      if (lastEventAtRef.current > 0) {
        const gap = now - lastEventAtRef.current;
        if (gap > 0 && gap < 5000) {
          gapsRef.current.push(gap);
          if (gapsRef.current.length > 9) gapsRef.current.shift();
        }
      }
      lastEventAtRef.current = now;

      lastCornersRef.current = quad.corners.map((p) => ({ x: p.x, y: p.y }));
      // 흐린 프레임은 히트로 세지 않는다 — 리셋이 아니라 창 만료에 맡기는 기존 설계
      // 그대로, "선명한 프레임 3회" 로 조건만 강화된다. status 는 holding 유지
      // (카드는 잘 잡혀 있고 손떨림만 있는 상태라 정렬 안내로 바꾸면 오히려 혼란).
      if (quad.sharpness === undefined || quad.sharpness >= AUTO_SHUTTER_MIN_SHARPNESS) {
        hitsRef.current.push(now);
      }
      setStatus("holding");
      // 표시는 게이트와 같은 값을 본다 — 게이트가 닫혀 있으면(쿨다운 등) 가득 차지
      // 않게 눌러 "다 찼는데 안 찍힌다"를 막는다
      const filled = Math.min(1, hitsRef.current.length / AUTO_SHUTTER_REQUIRED_HITS);
      const gateOpen = !opts.matchGate || opts.matchGate();
      progressRef.current = gateOpen ? filled : Math.min(filled, 0.9);
      // autoFire=false 면 판정만 하고 촬영하지 않는다 — 히트는 창 만료로 자연 소멸
      if (!opts.autoFire || hitsRef.current.length < AUTO_SHUTTER_REQUIRED_HITS) return;
      // 기하가 충족돼도 매칭 확신이 없으면 찍지 않는다. ⚠ 히트를 비우지 않는다 —
      // 창 만료에만 맡겨야 확신이 서는 즉시 다음 프레임에서 발화한다(비우면 카드를
      // 잘 대고 있는데도 3프레임을 매번 다시 모아야 해서 셔터가 눈에 띄게 늦어진다).
      if (!gateOpen) return;
      // 촬영 중에는 가득 유지 — 여기서 0 으로 떨어뜨리면 셔터음과 동시에 테두리가
      // 비어 "찍고 나서 푼다"로 보인다. 이후 판정(confirmBusy)이 이어받는다.
      progressRef.current = 1;
      capturingRef.current = true; // single-flight — 이후 이벤트 무시
      // 잠금 자동 해제 — fire 가 어떤 이유로든 안 끝나도 셔터가 영구히 죽지 않게
      if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = setTimeout(() => {
        nativeWarn("scan:autoShutter", "capture timed out — unlocking shutter");
        capturingRef.current = false;
        unlockTimerRef.current = null;
      }, AUTO_SHUTTER_CAPTURE_TIMEOUT_MS);
      hitsRef.current = [];
      void fire();
    },
    [fire, suspendedRef],
  );

  return { status, handleDetection, progressRef, resetHits };
}
